import { useEffect, useRef } from "react";

const IS_DEV = process.env.NODE_ENV !== "production";
const RECONNECT_DELAY_MS = 3000;

// Dev-only diagnostics — silent in production builds.
const debug = {
  error: (...args) => { if (IS_DEV) console.error(...args); },  // eslint-disable-line no-console
  warn:  (...args) => { if (IS_DEV) console.warn(...args); },   // eslint-disable-line no-console
};

/**
 * Subscribe to a backend WebSocket at path `/api/ws/...`.
 * `path` example: "business" or "status/<request_id>".
 * `onMessage` is called with parsed JSON payloads.
 * Auto-reconnects on unexpected close with 3s backoff.
 *
 * `onMessage` is intentionally stored in a ref so its identity doesn't reset
 * the socket connection. Only `path` and `enabled` are effect dependencies —
 * this is deliberate.
 */
export function useSocket(path, onMessage, enabled = true) {
  const wsRef = useRef(null);
  const handlerRef = useRef(onMessage);
  const closedRef = useRef(false);
  handlerRef.current = onMessage;

  useEffect(() => {
    if (!enabled || !path) return undefined;
    closedRef.current = false;

    const backend = process.env.REACT_APP_BACKEND_URL || "";
    const wsBase = backend.replace(/^http/i, "ws");
    let retryTimer = null;

    const connect = () => {
      if (closedRef.current) return;
      const url = `${wsBase}/api/ws/${path}`;
      const ws = new WebSocket(url);
      wsRef.current = ws;
      ws.onmessage = (ev) => {
        try {
          const data = JSON.parse(ev.data);
          handlerRef.current && handlerRef.current(data);
        } catch (err) {
          debug.error("[useSocket] bad message JSON", err);
        }
      };
      ws.onclose = () => {
        if (!closedRef.current) {
          retryTimer = setTimeout(connect, RECONNECT_DELAY_MS);
        }
      };
      ws.onerror = (err) => {
        debug.warn("[useSocket] connection error", err?.message || err);
        try {
          ws.close();
        } catch (closeErr) {
          debug.warn("[useSocket] close after error failed", closeErr);
        }
      };
    };

    connect();

    return () => {
      closedRef.current = true;
      if (retryTimer) clearTimeout(retryTimer);
      if (wsRef.current) {
        try {
          wsRef.current.close();
        } catch (err) {
          debug.warn("[useSocket] close on cleanup failed", err);
        }
      }
    };
  }, [path, enabled]); // eslint-disable-line react-hooks/exhaustive-deps -- handler is captured via handlerRef; changing WS on every handler identity would thrash the connection
}
